import {Dispatch, Reducer} from "react";
import {AsyncActionHandlers} from "use-reducer-async";
import {
  AssignJobsToManifestMutationFn,
  JobAssignmentUpdate,
  Job,
  ReassignJobStopsMutationFn,
  Driver,
  SkillOverrideNoteInput
} from "../generated/graphql";
import {isTerminalResult, JobAssignmentSubmissionResult} from "../utils/assignment/JobAssignmentEventHandler";
import {
  createAssignmentFromUpdate,
  findAssignmentFromUpdate,
  findAssignmentFromUpdatePredicate,
  PendingAssignment
} from "../utils/assignment/PendingAssignment";
import {
  showReassignmentErrorToast,
  showAssignmentToastForReassignment,
  showAssignmentToast
} from "../utils/toasters/toaster";
import {AuthState} from "./AuthProvider";
import log from "loglevel";
import {ManifestDetailsActions} from "./ManifestDetailsProvider";
import {TManifestEnhanced, TManifestStopEnhanced} from "../components/common/types";

type AssignmentState = {
  selectedJobs: Job[];
  jobsRequirements: Map<number, JobRequirement>;
  pendingActions: PendingAssignment[];
  selectedManifest: TManifestEnhanced | undefined;
  originManifest: TManifestEnhanced | undefined;
  selectedStops: TManifestStopEnhanced[];
  highlightManifests: number[] | undefined;
  splitJobDriver?: Driver;
  reassignJobsRequirements: Map<number, JobRequirement>;
};

type AssignmentPayload = {
  assignFunction: AssignJobsToManifestMutationFn;
  authState: AuthState;
  authToken: string;
  isFromGlobalFind?: boolean;
  selectedManifest?: TManifestEnhanced;
  selectedJobs?: Job[];
  skillOverrideNotes?: SkillOverrideNoteInput[];
};

type ReassignJobStopsPayload = {
  reassignFunction: ReassignJobStopsMutationFn;
  jobStopIds: number | number[];
  keepManifestSequence: boolean;
  fromManifestDriverId: number;
  authState: AuthState;
  authToken: string;
  manifestDetailsDispatch: Dispatch<ManifestDetailsActions>;
  skillOverrideNotes?: SkillOverrideNoteInput[];
};

export type JobRequirement = {
  id: number;
  name: string;
};

type AssignmentActions =
  | {type: "SetSelectedJobs"; payload: Job[]}
  | {type: "SetSelectedJob"; payload: Job}
  | {type: "SetSelectedStops"; payload: TManifestStopEnhanced[]}
  | {type: "SetSelectedManifest"; payload: TManifestEnhanced | undefined}
  | {type: "SetOriginManifest"; payload: TManifestEnhanced | undefined}
  | {type: "SetDisabledManifestIds"; payload: number[]}
  | {type: "SetHighlightManifest"; payload: number[]}
  | {type: "SetJobsRequirements"; payload: Map<number, JobRequirement>}
  | {type: "SetReassignJobsRequirements"; payload: Map<number, JobRequirement>}
  | {type: "ClearSelectionAndSetPendingAction"; payload: PendingAssignment | PendingAssignment[]}
  | {type: "UpdatePendingActionsStatus"; payload: JobAssignmentUpdate}
  | {type: "CleanStalePendingActions"}
  | {type: "SetSplitJobDriver"; payload: Driver | undefined}
  | {type: "ClearSelections"};

type AssignmentAsyncActions =
  | {type: "AssignJobsToManifest"; payload: AssignmentPayload}
  | {type: "ReassignJobStops"; payload: ReassignJobStopsPayload};

const AssignmentReducer = (state: AssignmentState, action: AssignmentActions): AssignmentState => {
  switch (action.type) {
    case "SetSelectedJobs": {
      return {
        ...state,
        selectedJobs: action.payload
      };
    }

    case "SetSelectedJob": {
      return {
        ...state,
        selectedJobs: [action.payload]
      };
    }

    case "SetSelectedStops": {
      return {
        ...state,
        selectedStops: action.payload
      };
    }

    case "SetOriginManifest": {
      return {
        ...state,
        originManifest: action.payload
      };
    }

    case "SetSelectedManifest": {
      const alreadySelected =
        state.selectedManifest && state.selectedManifest.manifestDriverId === action.payload?.manifestDriverId;
      if (alreadySelected) {
        return {
          ...state,
          selectedManifest: undefined
        };
      } else {
        return {
          ...state,
          selectedManifest: action.payload
        };
      }
    }

    case "SetHighlightManifest": {
      return {
        ...state,
        highlightManifests: action.payload
      };
    }

    case "SetJobsRequirements": {
      return {
        ...state,
        jobsRequirements: action.payload
      };
    }
    case "SetReassignJobsRequirements": {
      return {
        ...state,
        reassignJobsRequirements: action.payload
      };
    }

    case "ClearSelectionAndSetPendingAction": {
      const pendingActions = Array.isArray(action.payload) ? action.payload : [action.payload];
      return {
        ...state,
        selectedJobs: [],
        selectedStops: [],
        selectedManifest: undefined,
        jobsRequirements: new Map(),
        reassignJobsRequirements: new Map(),
        pendingActions: [...state.pendingActions, ...pendingActions]
      };
    }

    case "UpdatePendingActionsStatus": {
      const update = action.payload;
      const updateStatus =
        JobAssignmentSubmissionResult[update.submissionResult as keyof typeof JobAssignmentSubmissionResult];

      const updatedActions = state.pendingActions.map((pa) => {
        if (findAssignmentFromUpdatePredicate(pa, update)) {
          return {
            ...pa,
            status: updateStatus,
            manifestDriverId: update.manifestDriverId ?? pa.manifestDriverId,
            updatedAt: Date.now()
          };
        }
        return pa;
      });

      const existing = findAssignmentFromUpdate(state.pendingActions, update);
      if (!existing) {
        updatedActions.push(createAssignmentFromUpdate(update));
      }

      const nonTerminalActions = updatedActions.filter((pa) => !isTerminalResult(pa));

      return {
        ...state,
        pendingActions: nonTerminalActions
      };
    }

    case "CleanStalePendingActions": {
      const now = Date.now();
      // DP-3175 B06: also expire non-terminal actions older than 3 minutes. The
      // AssignmentSubscription may miss events, leaving actions stuck in
      // PENDING or SUBMISSION_SUCCESS. 3 minutes covers the pipeline's
      // assign + replicate + resequence + replicate + ETA cycle.
      const STALE_TIMEOUT_MS = 180_000;
      const isStale = (pa: PendingAssignment) =>
        (pa.status === JobAssignmentSubmissionResult.PENDING ||
          pa.status === JobAssignmentSubmissionResult.SUBMISSION_SUCCESS) &&
        now - pa.assignmentDateTime > STALE_TIMEOUT_MS;
      const liveActions = state.pendingActions.filter((pa) => !isTerminalResult(pa) && !isStale(pa));
      if (liveActions.length === state.pendingActions.length) {
        return state;
      }
      return {
        ...state,
        pendingActions: liveActions
      };
    }

    case "SetSplitJobDriver": {
      return {
        ...state,
        splitJobDriver: action.payload
      };
    }

    case "ClearSelections": {
      return {
        ...state,
        selectedJobs: [],
        selectedStops: [],
        selectedManifest: undefined,
        jobsRequirements: new Map(),
        reassignJobsRequirements: new Map()
      };
    }

    default:
      return state;
  }
};

const asyncActionHandlers: AsyncActionHandlers<Reducer<AssignmentState, AssignmentActions>, AssignmentAsyncActions> = {
  AssignJobsToManifest:
    ({dispatch, getState}) =>
    async (action) => {
      let state: AssignmentState;
      if (action.payload.isFromGlobalFind) {
        const selectedManifest = action.payload?.selectedManifest;
        const selectedJobs = action.payload?.selectedJobs ?? [];
        state = {
          ...getState(),
          selectedJobs: selectedJobs,
          selectedManifest: selectedManifest
        };
      } else {
        state = getState();
      }
      if (!state.selectedManifest || !state.selectedJobs) return;

      await action.payload.assignFunction({
        variables: {
          manifestDriverId: state.selectedManifest.manifestDriverId,
          jobIds: state.selectedJobs.flatMap((job) => job.jobId),
          ...(action.payload.skillOverrideNotes?.length ? {skillOverrideNotes: action.payload.skillOverrideNotes} : {})
        },
        onError: (error) => log.error(error)
      });

      showAssignmentToast(
        state.selectedJobs,
        state.selectedManifest,
        action.payload.authState,
        action.payload.authToken
      );

      dispatch({
        type: "ClearSelectionAndSetPendingAction",
        payload: buildPendingAction(state, "assign")
      });
    },
  ReassignJobStops:
    ({dispatch, getState}) =>
    async (action) => {
      const state = getState();
      const selectedManifestDriver = state.selectedManifest;

      await action.payload.reassignFunction({
        variables: {
          manifestDriverId: state.selectedManifest?.manifestDriverId as number,
          fromManifestDriverId: action.payload.fromManifestDriverId,
          jobStopIds: action.payload.jobStopIds,
          keepManifestSequence: action.payload.keepManifestSequence,
          ...(action.payload.skillOverrideNotes?.length ? {skillOverrideNotes: action.payload.skillOverrideNotes} : {})
        },
        onError: (error) => {
          log.error(error);
          if (state.selectedStops?.length ?? 0 > 1) {
            showReassignmentErrorToast(state.originManifest, action.payload.manifestDetailsDispatch);
          }
        }
      });
      showAssignmentToastForReassignment(
        state.selectedStops!,
        selectedManifestDriver!,
        action.payload.authState,
        action.payload.authToken
      );

      dispatch({
        type: "ClearSelectionAndSetPendingAction",
        payload: buildPendingAction(state, "reassign")
      });
    }
};

export const buildPendingAction = (state: AssignmentState, mode: "assign" | "reassign"): PendingAssignment => ({
  jobIds: mode === "assign" ? state.selectedJobs.map((x) => x.jobId) : [],
  jobStopIds: mode === "reassign" ? state.selectedStops.map((x) => x.jobStopId) : [],
  manifestDriverId: state.selectedManifest?.manifestDriverId as number,
  user: "",
  assignmentDateTime: Date.now(),
  status: JobAssignmentSubmissionResult.PENDING,
  updatedAt: Date.now()
});

export default AssignmentReducer;
export {asyncActionHandlers};
export type {AssignmentActions, AssignmentState, AssignmentAsyncActions, PendingAssignment};
